// DNA Pay · Gestão de Títulos 2.0 — KPIs, filtros inteligentes e visão executiva
// Fonte única: GET /api/finance/titles/board (items + summary + facets). Nenhum cálculo no frontend.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus, Search, Filter, FileText, Eye, CreditCard, QrCode, Barcode, Loader2, RefreshCw,
  AlertTriangle, Copy, Trash2, Edit, Archive, Undo2, RotateCcw, FileDown, Mail, Send,
  MoreHorizontal, ArrowUpDown, ChevronLeft, ChevronRight, Wallet, Handshake, ShieldAlert, Receipt,
  Wrench,
} from 'lucide-react';
import ReconcilePreviewDialog from './ReconcilePreviewDialog';
import PurgeBulkDialog from './PurgeBulkDialog';
import { toast } from 'sonner';
import { useTituloActions } from '@/hooks/useTituloActions';
import api from '@/lib/api';
import TitulosKpiCards from './components/TitulosKpiCards';
import TitulosFilters from './components/TitulosFilters';
import TituloDetailDrawer from './components/TituloDetailDrawer';
import TituloStatusBadge from './components/TituloStatusBadge';
import WhatsappSendDialog from './components/WhatsappSendDialog';
import ReciboUnificadoDialog from './components/ReciboUnificadoDialog';
import PaymentSettlementModal from '@/components/financeiro/PaymentSettlementModal';
import BatchSettlementModal from '@/components/financeiro/BatchSettlementModal';
import ReconciliarDatasDialog from './components/ReconciliarDatasDialog';
import TituloRecebimentos from './components/TituloRecebimentos';
import DeclaracaoIrDialog from './components/DeclaracaoIrDialog';
import { PlanoAlunoModal } from './components/PlanoAlunoModal';
import { ParcelasDivergentesDialog } from './components/ParcelasDivergentesDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FILTERS = {
  q: '', status: 'all', period: 'all', category: 'all', source: 'all',
  class_group_id: 'all', quick: '', date_from: '', date_to: '',
};

const initialFilters = () => {
  const sp = new URLSearchParams(window.location.search);
  const patch = {};
  ['q', 'status', 'period', 'category', 'source', 'quick', 'class_group_id'].forEach((k) => {
    const v = sp.get(k);
    if (v) patch[k] = v;
  });
  return { ...EMPTY_FILTERS, ...patch };
};

export default function DnaPayTitulos() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [titulos, setTitulos] = useState([]);
  const [summary, setSummary] = useState(null);
  const [facets, setFacets] = useState({});
  const [classGroups, setClassGroups] = useState([]);
  const [alunos, setAlunos] = useState([]);

  const [filters, setFilters] = useState(initialFilters);
  const [sort, setSort] = useState({ field: 'due_date', dir: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [detailId, setDetailId] = useState(null);
  const [planoTitulo, setPlanoTitulo] = useState(null);
  const [showParcelasDiv, setShowParcelasDiv] = useState(false);
  const [waTitleId, setWaTitleId] = useState(null);
  const [unified, setUnified] = useState(null);
  const [irDecl, setIrDecl] = useState(null);

  // Modais legados preservados
  const [showNovoTitulo, setShowNovoTitulo] = useState(false);
  const [showPagamento, setShowPagamento] = useState(false);
  const [selectedTitulo, setSelectedTitulo] = useState(null);
  const [metodoPagamento, setMetodoPagamento] = useState('pix');
  const [pixData, setPixData] = useState(null);
  const [boletoData, setBoletoData] = useState(null);
  const [novoTitulo, setNovoTitulo] = useState({
    aluno_id: '', descricao: '', categoria: 'mensalidade',
    valor_original: '', desconto: 0, vencimento: '', competencia: '',
  });

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchOpen, setBatchOpen] = useState(false);
  const [datasOpen, setDatasOpen] = useState(false);
  const [recebimentosId, setRecebimentosId] = useState(null);
  const [settle, setSettle] = useState(null);
  const [renegDialog, setRenegDialog] = useState({ open: false, titulo: null, busy: false, novo_valor: '', novo_vencimento: '', motivo: '' });
  const [showExcluded, setShowExcluded] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [showPurged, setShowPurged] = useState(false);
  const [purgedList, setPurgedList] = useState([]);
  const [excludedItems, setExcludedItems] = useState([]);
  const [loadingExcluded, setLoadingExcluded] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, scope: 'selected', preview: null, loading: false, reason: '', studentId: '' });
  const [excludedFilters, setExcludedFilters] = useState({ deleted_from: '', deleted_to: '', deleted_by: '', search: '', student_id: '' });
  const [reconcile, setReconcile] = useState({ pending: 0, missing: 0, busy: false });

  const tokenAuth = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : String(dateStr);
  };

  const dirty = useMemo(
    () => JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS),
    [filters]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({
        sort: sort.field, direction: sort.dir, page: String(page), page_size: String(pageSize),
      });
      if (filters.q) p.set('q', filters.q);
      if (filters.status !== 'all') p.set('status', filters.status);
      if (filters.period !== 'all') p.set('period', filters.period);
      if (filters.category !== 'all') p.set('category', filters.category);
      if (filters.source !== 'all') p.set('source', filters.source);
      if (filters.class_group_id !== 'all') p.set('class_group_id', filters.class_group_id);
      if (filters.quick) p.set('quick', filters.quick);
      if (filters.date_from) p.set('date_from', filters.date_from);
      if (filters.date_to) p.set('date_to', filters.date_to);

      const res = await fetch(`${API_URL}/api/finance/titles/board?${p}`, { headers: tokenAuth() });
      if (!res.ok) throw new Error('falha');
      const data = await res.json();
      setTitulos(data.items || []);
      setSummary(data.summary || null);
      setFacets(data.facets || {});
      setTotalPages(data.total_pages || 1);
      setClassGroups(data.filter_options?.class_groups || []);
    } catch (e) {
      failToast(e, 'erro ao carregar títulos');
    } finally {
      setLoading(false);
    }
  }, [filters, sort, page, pageSize]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { setPage(1); }, [filters, pageSize]);

  useEffect(() => {
    fetch(`${API_URL}/api/students?limit=1000`, { headers: tokenAuth() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setAlunos(Array.isArray(d) ? d : (d.students || [])))
      .catch(() => setAlunos([]));
  }, []);

  const loadReconcile = useCallback(() => {
    fetch(`${API_URL}/api/enrollment/titles/reconcile-preview`, { headers: tokenAuth() })
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setReconcile(s => ({
        ...s,
        pending: d.totals?.processes_pending || 0,
        missing: d.totals?.created || 0,
      })))
      .catch(() => {});
  }, []);
  useEffect(() => { loadReconcile(); }, [loadReconcile]);

  const runReconcile = async () => {
    setReconcile(s => ({ ...s, busy: true }));
    try {
      const r = await fetch(`${API_URL}/api/enrollment/titles/reconcile`, { method: 'POST', headers: tokenAuth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      toast.success(`Títulos gerados: ${d.totals?.created || 0} · atualizados: ${d.totals?.updated || 0}`);
      loadReconcile();
      loadData();
    } catch (e) {
      failToast(e, 'falha ao reconciliar títulos');
    } finally {
      setReconcile(s => ({ ...s, busy: false }));
    }
  };

  const setF = (patch) => setFilters(f => ({ ...f, ...patch }));
  const resetFilters = () => setFilters(EMPTY_FILTERS);

  // Card clicável = filtro rápido
  const pickCard = (key) => {
    if (key === 'all' || key === 'aberto') return setF({ status: 'all', quick: '' });
    if (key === 'pagos' || key === 'pago') return setF({ status: 'pago', quick: '' });
    if (key === 'a_vencer') return setF({ status: 'a_vencer', quick: '' });
    if (key === 'vencido') return setF({ status: 'vencido', quick: '' });
  };

  const toggleSort = (field) => {
    setSort(s => s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  };

  const { openAction: openTituloAction, dialogNode: tituloActionDialog } = useTituloActions({ onChanged: loadData });

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const toggleSelectAll = () => {
    if (titulos.length > 0 && titulos.every(t => selectedIds.has(t.id))) setSelectedIds(new Set());
    else setSelectedIds(new Set(titulos.map(t => t.id)));
  };

  const currentFilters = () => {
    const f = {};
    if (filters.status !== 'all') f.status = filters.status === 'a_vencer' ? 'pendente' : filters.status;
    if (filters.category !== 'all') f.category = filters.category;
    return f;
  };

  const handleCriarTitulo = async () => {
    if (!novoTitulo.aluno_id || !novoTitulo.descricao || !novoTitulo.valor_original || !novoTitulo.vencimento) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/dnapay/billing/titulos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...tokenAuth() },
        body: JSON.stringify({
          ...novoTitulo,
          valor_original: parseFloat(novoTitulo.valor_original),
          desconto: parseFloat(novoTitulo.desconto) || 0,
        }),
      });
      if (response.ok) {
        toast.success('Título criado com sucesso!');
        setShowNovoTitulo(false);
        setNovoTitulo({ aluno_id: '', descricao: '', categoria: 'mensalidade', valor_original: '', desconto: 0, vencimento: '', competencia: '' });
        loadData();
      } else {
        const error = await response.json();
        failToast(error, 'falha ao criar título');
      }
    } catch (e) {
      failToast(e, 'erro ao criar título');
    }
  };

  const handleGerarPagamento = async () => {
    if (!selectedTitulo || !metodoPagamento) return;
    try {
      const response = await fetch(`${API_URL}/api/dnapay/pagamentos/${selectedTitulo.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...tokenAuth() },
        body: JSON.stringify({ metodo: metodoPagamento }),
      });
      if (response.ok) {
        const data = await response.json();
        if (metodoPagamento === 'pix') { setPixData(data.dados); toast.success('PIX gerado com sucesso!'); }
        else if (metodoPagamento === 'boleto') { setBoletoData(data.dados); toast.success('Boleto gerado com sucesso!'); }
        else if (metodoPagamento.includes('cartao')) { window.open(data.dados.link_pagamento, '_blank'); toast.success('Link de pagamento gerado!'); }
        loadData();
      } else {
        const error = await response.json();
        failToast(error, 'falha ao gerar pagamento');
      }
    } catch (e) {
      failToast(e, 'erro ao gerar pagamento');
    }
  };

  const openDeleteDialog = async (scope, opts = {}) => {
    setDeleteDialog({ open: true, scope, preview: null, loading: true, reason: '', studentId: opts.student_id || '' });
    try {
      const body = {};
      if (scope === 'selected') body.title_ids = Array.from(selectedIds);
      else if (scope === 'student') { body.student_id = opts.student_id; body.filters = currentFilters(); }
      else if (scope === 'filter') body.filters = currentFilters();
      const ep = scope === 'student' ? '/api/financial/titles/delete-by-student?dry_run=true' : '/api/financial/titles/delete-bulk?dry_run=true';
      const res = await fetch(`${API_URL}${ep}`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...tokenAuth() }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(errMsg(data, 'falha ao calcular impacto'));
      setDeleteDialog(d => ({ ...d, preview: data, loading: false }));
    } catch (e) {
      failToast(e, 'falha ao calcular impacto');
      setDeleteDialog({ open: false, scope: 'selected', preview: null, loading: false, reason: '', studentId: '' });
    }
  };

  const confirmDelete = async () => {
    const { scope, preview, reason, studentId } = deleteDialog;
    if (!reason.trim()) { toast.error('Informe o motivo'); return; }
    if (!preview || (preview.would_delete || 0) === 0) return;
    const counts = preview.counts || {};
    const blockers = [];
    if (counts.with_nfse > 0) blockers.push(`${counts.with_nfse} com NFS-e`);
    if (blockers.length) {
      toast.error(`Movimentação ATIVA (${blockers.join(', ')}). Cancele a NFS-e antes. Pagamento já estornado não bloqueia.`);
      return;
    }
    setDeleteDialog(d => ({ ...d, loading: true }));
    try {
      const body = { reason };
      if (scope === 'selected') body.title_ids = Array.from(selectedIds);
      else if (scope === 'student') { body.student_id = studentId; body.filters = currentFilters(); }
      else if (scope === 'filter') body.filters = currentFilters();
      const ep = scope === 'student' ? '/api/financial/titles/delete-by-student?dry_run=false' : '/api/financial/titles/delete-bulk?dry_run=false';
      const res = await fetch(`${API_URL}${ep}`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...tokenAuth() }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(errMsg(data, 'falha na exclusão'));
      toast.success(`${data.deleted} título(s) excluído(s) (financeiro).`);
      setDeleteDialog({ open: false, scope: 'selected', preview: null, loading: false, reason: '', studentId: '' });
      setSelectedIds(new Set());
      await loadData();
      if (showExcluded) loadExcludedItems();
    } catch (e) {
      failToast(e, 'falha na exclusão');
      setDeleteDialog(d => ({ ...d, loading: false }));
    }
  };

  const loadExcludedItems = async () => {
    setLoadingExcluded(true);
    try {
      const params = new URLSearchParams({ limit: '500' });
      Object.entries(excludedFilters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const res = await fetch(`${API_URL}/api/financial/titles/excluded?${params.toString()}`, { headers: tokenAuth() });
      const data = await res.json();
      setExcludedItems(data?.items || []);
    } catch { setExcludedItems([]); }
    finally { setLoadingExcluded(false); }
  };

  const restoreTitles = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Restaurar ${ids.length} título(s)? O impacto em DRE/Caixa será recomputado.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/financial/titles/restore`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...tokenAuth() },
        body: JSON.stringify({ title_ids: ids, reason: 'restauracao manual' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(errMsg(data, 'falha ao restaurar'));
      toast.success(`${data.restored} título(s) restaurado(s).`);
      loadExcludedItems();
      loadData();
    } catch (e) { failToast(e, 'falha ao restaurar'); }
  };

  useEffect(() => {
    if (showExcluded) loadExcludedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExcluded, excludedFilters]);

  const copyLinha = async (t) => {
    let linha = t.linha_digitavel;
    if (!linha) {
      try {
        const { data } = await api.get(`/dnapay/titulos/${t.title_id}/boleto`);
        linha = data.linha_digitavel;
      } catch { /* segue para o aviso abaixo */ }
    }
    if (!linha) {
      toast.warning('Código de pagamento ainda não disponível. Use "Gerar/reprocessar boleto".');
      return;
    }
    navigator.clipboard.writeText(String(linha).replace(/\D/g, ''));
    toast.success('Código de pagamento copiado (47 dígitos).');
  };

  const regerarBoleto = async (t) => {
    toast.info('Preparando boleto…');
    try {
      const { data } = await api.post(`/dnapay/titulos/${t.title_id}/boleto/gerar?force=true`);
      toast.success(`Boleto pronto · Nosso Número ${data.nosso_numero}`);
      loadData();
    } catch (e) {
      toast.error(errMsg(e, 'Não foi possível gerar o boleto'));
    }
  };

  // Mensagem de erro sempre legível — nunca [object Object]
  const errMsg = (e, fallback = 'erro inesperado') => {
    const d = e?.response?.data?.detail ?? e?.detail ?? e?.message ?? e;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join('; ');
    if (d && typeof d === 'object') return d.msg || d.detail || JSON.stringify(d);
    return fallback;
  };
  const failToast = (e, fallback) => toast.error(`Não foi possível executar esta ação: ${errMsg(e, fallback)}`);

  const openTitlePage = (t) => {
    if (!t?.title_id) { toast.error('Não foi possível executar esta ação: título sem identificador (title_id).'); return; }
    navigate(`/v2/financeiro/dnapay/titulos/${t.title_id}`);
  };

  const openBoleto = async (t) => {
    try {
      const r = await fetch(`${API_URL}/api/financial/titles/${t.title_id}/boleto`, { headers: tokenAuth() });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.warning(errMsg(j, 'Boleto PDF indisponível'));
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      failToast(e, 'Boleto PDF indisponível');
    }
  };

  const [envioLote, setEnvioLote] = useState(null);

  const enviarBoletosEmMassa = async () => {
    const ids = Array.from(selectedIds);
    const comp = !ids.length
      ? window.prompt('Competência para enviar (MM/AAAA):', '')
      : null;
    if (!ids.length && !comp) return;
    const alvo = ids.length ? `${ids.length} título(s) selecionado(s)` : `competência ${comp}`;
    if (!window.confirm(`Enviar o boleto por e-mail aos responsáveis de ${alvo}?`)) return;
    try {
      const { data } = await api.post('/dnapay/boletos/enviar-email-lote',
        ids.length ? { title_ids: ids } : { competencia: comp });
      setEnvioLote({ ...data, enviados: 0 });
      toast.info(`Enviando ${data.total} boleto(s) por e-mail…`);
      const acompanhar = async () => {
        const { data: st } = await api.get(`/dnapay/boletos/enviar-email-lote/${data.job_id}`);
        setEnvioLote(st);
        if (st.status === 'em_execucao') { setTimeout(acompanhar, 4000); return; }
        toast.success(`${st.enviados} boleto(s) enviado(s)`
          + (st.falhas?.length ? ` · ${st.falhas.length} falha(s)` : ''));
      };
      setTimeout(acompanhar, 4000);
    } catch (e) {
      failToast(e, 'não foi possível iniciar o envio em massa');
    }
  };

  const sendEmail = async (t) => {
    if (!window.confirm('Enviar o boleto por e-mail ao responsável, com o código de pagamento no corpo?')) return;
    toast.info('Preparando e enviando o boleto…');
    try {
      const r = await api.post(`/dnapay/titulos/${t.title_id}/boleto/enviar-email`, {});
      toast.success(`Boleto ${r.data.nosso_numero} enviado para ${r.data.email}`
        + (r.data.com_anexo ? ' com PDF em anexo' : ''));
    } catch (e) {
      try {
        const r2 = await api.post(`/finance/titles/${t.title_id}/send-email`, {});
        toast.success(`E-mail enviado para ${r2.data.email}`);
      } catch (e2) {
        failToast(e, 'não foi possível enviar o boleto por e-mail');
      }
    }
  };

  // Aliases legados exigidos pelo hook de ações (edit/cancel/estornar) — nenhum dado novo, só nomes
  const legacyShape = (t) => ({
    ...t,
    valor_original: t.amount_original,
    valor_final: t.amount_display,
    vencimento: t.due_date,
    descricao: t.description,
    categoria_id: t.category,
    competencia: t.competence,
  });

  const openSettle = (t) => {
    const saldoAberto = Number(t.balance ?? t.amount_display ?? 0) > 0.009;
    if (['cancelado', 'renegociado'].includes(t.status) || (t.status === 'pago' && !saldoAberto)) {
      toast.warning(`Título ${t.status} não recebe novo recebimento.`);
      return;
    }
    setSettle({ id: t.title_id || t.id, description: t.description || t.descricao });
  };

  // 1 título selecionado → baixa individual; 2+ → recebimento agrupado
  const openSettleSelection = () => {
    if (selectedIds.size === 1) {
      const t = titulos.find((x) => selectedIds.has(x.id));
      if (t) openSettle(t);
      return;
    }
    setBatchOpen(true);
  };

  const openReneg = (t) => {    if (['pago', 'cancelado', 'renegociado'].includes(t.status)) {
      toast.warning(`Título ${t.status} não pode ser renegociado.`);
      return;
    }
    setRenegDialog({
      open: true, titulo: t, busy: false,
      novo_valor: String(t.balance ?? t.amount_display ?? ''), novo_vencimento: '', motivo: '',
    });
  };

  const confirmReneg = async () => {
    const { titulo, novo_valor, novo_vencimento, motivo } = renegDialog;
    const v = parseFloat(String(novo_valor).replace(',', '.'));
    if (!v || v <= 0) { toast.error('Informe o novo valor.'); return; }
    if (!novo_vencimento) { toast.error('Informe o novo vencimento.'); return; }
    if (!motivo.trim() || motivo.trim().length < 3) { toast.error('Motivo obrigatório (mínimo 3 caracteres).'); return; }
    setRenegDialog(d => ({ ...d, busy: true }));
    try {
      await api.post(`/financial/titulos/${titulo.title_id}/renegociar`, {
        novo_valor: v, novo_vencimento, motivo,
      });
      toast.success('Renegociação concluída · título anterior marcado como renegociado');
      setRenegDialog({ open: false, titulo: null, busy: false, novo_valor: '', novo_vencimento: '', motivo: '' });
      loadData();
    } catch (e) {
      failToast(e, 'falha ao renegociar');
      setRenegDialog(d => ({ ...d, busy: false }));
    }
  };

  const SortHead = ({ field, children, className = '' }) => (
    <TableHead className={className}>
      <button type="button" onClick={() => toggleSort(field)} data-testid={`sort-${field}`}
        className="inline-flex items-center gap-1 font-semibold text-slate-600 hover:text-violet-700">
        {children}
        <ArrowUpDown className={`w-3 h-3 ${sort.field === field ? 'text-violet-600' : 'text-slate-300'}`} />
      </button>
    </TableHead>
  );

  // Recibo em JPEG de qualquer título pago/parcial (mesmo layout do recibo da matrícula)
  const verRecibo = async (titulo) => {
    try {
      const tid = titulo.title_id || titulo.id;
      const r = await api.get(`/financial/titulos/${tid}/recibo.jpg`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível gerar o recibo.');
    }
  };

  const enviarRecibo = async (titulo) => {
    try {
      const tid = titulo.title_id || titulo.id;
      const { data } = await api.get(`/financial/titulos/${tid}/recibo/share`);
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.image_path}`;
      const msg = encodeURIComponent(`${data.text}${url}`);
      window.open(data.phone ? `https://wa.me/${data.phone}?text=${msg}` : `https://wa.me/?text=${msg}`, '_blank');
      toast.success(data.phone
        ? `Recibo pronto para ${data.payer_name || 'o responsável'} — confirme o envio no WhatsApp.`
        : 'Responsável sem telefone cadastrado: escolha o contato no WhatsApp.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível preparar o recibo.');
    }
  };


  const ActionsMenu = ({ titulo }) => (    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={`actions-${titulo.id}`}>
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={() => setDetailId(titulo.id)} data-testid={`action-detail-${titulo.id}`}>
          <Eye className="w-4 h-4 mr-2" /> Ver detalhes
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openTitlePage(titulo)} data-testid={`action-page-${titulo.id}`}>
          <FileText className="w-4 h-4 mr-2" /> Abrir página do título
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setRecebimentosId(titulo.id)} data-testid={`action-recebimentos-${titulo.id}`}>
          <Wallet className="w-4 h-4 mr-2" /> Recebimentos do título
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {titulo.status !== 'cancelado' && (
          <DropdownMenuItem onClick={() => setPlanoTitulo(titulo)} data-testid={`action-edit-${titulo.id}`}>
            <Edit className="w-4 h-4 mr-2" /> Editar plano do aluno
          </DropdownMenuItem>
        )}
        {titulo.status !== 'cancelado' && (titulo.status !== 'pago'
          || Number(titulo.balance ?? 0) > 0.009) && (
          <>
            <DropdownMenuItem onClick={() => openSettle(titulo)} data-testid={`action-baixa-${titulo.id}`}>
              <Wallet className="w-4 h-4 mr-2" /> Registrar recebimento
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => { setSelectedTitulo(titulo); setShowPagamento(true); }} data-testid={`action-cobrar-${titulo.id}`}>
              <CreditCard className="w-4 h-4 mr-2" /> Cobrar (PIX / boleto / cartão)
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem onClick={() => openBoleto(titulo)} data-testid={`action-boleto-${titulo.id}`}>
          <FileDown className="w-4 h-4 mr-2" /> Ver boleto
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => copyLinha(titulo)} data-testid={`action-copy-${titulo.id}`}>
          <Copy className="w-4 h-4 mr-2" /> Copiar código para pagamento
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => regerarBoleto(titulo)} data-testid={`action-regerar-boleto-${titulo.id}`}>
          <FileDown className="w-4 h-4 mr-2" /> Gerar/reprocessar boleto
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setWaTitleId(titulo.title_id)} data-testid={`action-whatsapp-${titulo.id}`}>
          <Send className="w-4 h-4 mr-2" /> Enviar pelo WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => sendEmail(titulo)} data-testid={`action-email-${titulo.id}`}>
          <Mail className="w-4 h-4 mr-2" /> Enviar boleto por e-mail
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {['pago', 'parcial'].includes(titulo.status) && (
          <>
            <DropdownMenuItem onClick={() => verRecibo(titulo)} data-testid={`action-receipt-${titulo.id}`}>
              <Receipt className="w-4 h-4 mr-2" /> Recibo em JPEG
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => enviarRecibo(titulo)} data-testid={`action-receipt-wa-${titulo.id}`}>
              <Send className="w-4 h-4 mr-2" /> Enviar recibo no WhatsApp
            </DropdownMenuItem>
          </>
        )}
        {!!titulo.aluno_id && (
          <>
            <DropdownMenuItem data-testid={`action-receipt-unified-${titulo.id}`}
              onClick={() => setUnified({ id: titulo.aluno_id, name: titulo.aluno_nome })}>
              <Receipt className="w-4 h-4 mr-2" /> Recibo unificado do aluno
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`action-ir-${titulo.id}`}
              onClick={() => setIrDecl({ id: titulo.aluno_id, name: titulo.aluno_nome })}>
              <FileText className="w-4 h-4 mr-2" /> Declaração anual (IR)
            </DropdownMenuItem>
          </>
        )}
        {(titulo.status === 'pago' || titulo.status === 'parcial') && (
          <DropdownMenuItem onClick={() => openTituloAction('estornar', legacyShape(titulo))} data-testid={`action-reverse-${titulo.id}`}>
            <Undo2 className="w-4 h-4 mr-2" /> Estornar pagamento
          </DropdownMenuItem>
        )}
        {titulo.status === 'estornado' && (
          <DropdownMenuItem onClick={() => openTituloAction('reverter', legacyShape(titulo))}>
            <RotateCcw className="w-4 h-4 mr-2" /> Reverter estorno
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => openReneg(titulo)} data-testid={`action-renegotiate-${titulo.id}`}>
          <Handshake className="w-4 h-4 mr-2" /> Renegociar
        </DropdownMenuItem>
        {titulo.status === 'cancelado' ? (
          <DropdownMenuItem onClick={() => openTituloAction('restore', legacyShape(titulo))}>
            <RotateCcw className="w-4 h-4 mr-2" /> Restaurar
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={() => openTituloAction('cancel', legacyShape(titulo))} data-testid={`action-cancel-${titulo.id}`}>
            <Trash2 className="w-4 h-4 mr-2" /> Cancelar
          </DropdownMenuItem>
        )}
        <DropdownMenuItem className="text-rose-700" onClick={() => { setSelectedIds(new Set([titulo.id])); openDeleteDialog('selected'); }}>
          <Archive className="w-4 h-4 mr-2" /> Excluir / arquivar
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const ValueCell = ({ t }) => (
    <div className="leading-tight" data-testid={`valor-${t.id}`}
      title={[
        `Valor nominal: ${formatCurrency(t.nominal_amount ?? t.amount_original)}`,
        `Desconto: ${formatCurrency(t.discount_amount ?? t.discount ?? 0)}`,
        `Valor base: ${formatCurrency(t.principal_due ?? t.amount_display)}`,
        `Multa: ${formatCurrency(t.late_fee_amount ?? 0)}`,
        `Juros: ${formatCurrency(t.interest_amount ?? 0)}`,
        t.status === 'pago' || t.status === 'parcial'
          ? `Total pago: ${formatCurrency(t.total_received ?? t.paid_amount ?? 0)}`
          : `Total devido: ${formatCurrency((t.balance || 0) + (t.late_fee_amount || 0) + (t.interest_amount || 0))}`,
      ].join('\n')}>
      <p className="font-bold text-slate-900">{formatCurrency(t.amount_display)}</p>
      {t.discount > 0 && (
        <p className="text-[11px] text-emerald-600">
          desconto {formatCurrency(t.discount)} · líquido {formatCurrency(t.principal_due ?? t.amount_display)}
        </p>
      )}
      {((t.late_fee_amount || 0) + (t.interest_amount || 0)) > 0 && (
        <p className="text-[11px] text-amber-700" data-testid={`encargos-${t.id}`}>
          multa {formatCurrency(t.late_fee_amount)} · juros {formatCurrency(t.interest_amount)} ·
          {' '}total {formatCurrency(t.total_received ?? ((t.paid_amount || 0) + (t.late_fee_amount || 0) + (t.interest_amount || 0)))}
        </p>
      )}
      {(t.paid_amount || 0) > 0 && t.status !== 'pago' && (
        <p className="text-[11px] text-violet-600">pago {formatCurrency(t.paid_amount)}</p>
      )}
      {t.status === 'parcial' && (
        <p className="text-[11px] font-semibold text-rose-600">saldo {formatCurrency(t.balance)}</p>
      )}
    </div>
  );

  return (
    <div className="space-y-5 p-4 sm:p-6" data-testid="dnapay-titulos">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-violet-600" />
            Gestão de Títulos
          </h1>
          <p className="text-sm text-slate-500">Visão executiva de cobranças, recebimentos e inadimplência</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {!showExcluded && (
            <>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={selectedIds.size < 1} data-testid="btn-batch-settle"
                onClick={openSettleSelection}>
                <Wallet className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Registrar pagamento dos selecionados&nbsp;</span>
                <span className="sm:hidden">Registrar pagamento&nbsp;</span>({selectedIds.size})
              </Button>
              <Button variant="outline" className="border-violet-200 text-violet-700 hover:bg-violet-50"
                disabled={selectedIds.size === 0} data-testid="btn-boletos-pdf-lote"
                onClick={async () => {
                  try {
                    const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dnapay/boletos/pdf-lote`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', ...tokenAuth() },
                      body: JSON.stringify({ titulo_ids: Array.from(selectedIds) }),
                    });
                    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                    const blob = await resp.blob();
                    window.open(URL.createObjectURL(blob), '_blank');
                  } catch (e) { failToast(e, 'falha ao gerar PDF em lote'); }
                }}>
                <FileDown className="w-4 h-4 mr-2" /> Boletos ({selectedIds.size})
              </Button>
              <Button variant="outline" onClick={enviarBoletosEmMassa}
                disabled={envioLote?.status === 'em_execucao'}
                data-testid="btn-enviar-boletos-massa"
                className="border-sky-200 text-sky-700 hover:bg-sky-50">
                <Mail className="w-4 h-4 mr-2" />
                {envioLote?.status === 'em_execucao'
                  ? `Enviando ${envioLote.enviados}/${envioLote.total}…`
                  : `Enviar boletos por e-mail (${selectedIds.size || summary?.count || 0})`}
              </Button>
              <Button variant="outline" onClick={() => openDeleteDialog('selected')} disabled={selectedIds.size === 0}                data-testid="btn-delete-selected" className="border-rose-200 text-rose-700 hover:bg-rose-50">
                <Trash2 className="w-4 h-4 mr-2" /> Excluir selecionados ({selectedIds.size})
              </Button>
              <Button variant="outline" onClick={() => setPurgeOpen(true)} disabled={selectedIds.size === 0}
                data-testid="btn-purge-selected" className="border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100">
                <Trash2 className="w-4 h-4 mr-2" /> Excluir definitivamente ({selectedIds.size})
              </Button>
              <Button variant="outline" onClick={() => openDeleteDialog('filter')} disabled={(summary?.count ?? 0) === 0}
                data-testid="btn-delete-filter" className="border-amber-200 text-amber-700 hover:bg-amber-50">
                <Filter className="w-4 h-4 mr-2" /> Excluir filtro ({summary?.count ?? 0})
              </Button>
              <Button variant="outline" onClick={loadData} data-testid="btn-refresh">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => setReconcileOpen(true)} data-testid="btn-reconcile-preview-always">
            <RotateCcw className="w-4 h-4 mr-2" /> Prévia de reconciliação
          </Button>
          <Button variant="outline" onClick={() => setDatasOpen(true)} data-testid="btn-reconcile-dates"
            className="border-violet-200 text-violet-700 hover:bg-violet-50">
            <RotateCcw className="w-4 h-4 mr-2" /> Reconciliar datas financeiras
          </Button>
          <Button variant={showPurged ? 'default' : 'outline'} onClick={async () => {
            const next = !showPurged; setShowPurged(next);
            if (next) {
              try {
                const r = await fetch(`${API_URL}/api/finance/entries/purged/list`, { headers: tokenAuth() });
                const j = await r.json(); setPurgedList(j.items || []);
              } catch (e) { toast.error('Falha ao carregar purgados'); }
            }
          }} data-testid="btn-toggle-purged">
            <ShieldAlert className="w-4 h-4 mr-2" /> {showPurged ? 'Voltar' : 'Ver purgados'}
          </Button>
          <Button variant={showExcluded ? 'default' : 'outline'} onClick={() => setShowExcluded(s => !s)} data-testid="btn-toggle-excluded">
            <Archive className="w-4 h-4 mr-2" /> {showExcluded ? 'Voltar' : 'Ver excluídos'}
          </Button>
          {!showExcluded && (
            <Button variant="outline" onClick={() => setShowParcelasDiv(true)} data-testid="btn-parcelas-divergentes">
              <Wrench className="w-4 h-4 mr-2" /> Parcelas divergentes
            </Button>
          )}
          {!showExcluded && (
            <Button onClick={() => setShowNovoTitulo(true)} data-testid="btn-novo-titulo">
              <Plus className="w-4 h-4 mr-2" /> Novo Título
            </Button>
          )}
        </div>
      </div>

      {!showExcluded && selectedIds.size > 0 && (
        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 shadow-sm"
          data-testid="batch-action-bar">
          <div className="text-sm">
            <b className="text-emerald-900">{selectedIds.size} título(s) selecionado(s)</b>
            <span className="text-emerald-800">
              {' '}· Saldo total: <b>{formatCurrency(titulos.filter(t => selectedIds.has(t.id))
                .reduce((a, t) => a + Number(t.balance ?? t.amount_display ?? 0), 0))}</b>
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={selectedIds.size < 1} data-testid="btn-batch-settle-bar"
              onClick={openSettleSelection}>
              <Wallet className="w-4 h-4 mr-2" /> Registrar pagamento
            </Button>
            <Button variant="outline" data-testid="btn-clear-selection"
              onClick={() => setSelectedIds(new Set())}>
              Limpar seleção
            </Button>
          </div>
        </div>
      )}

      {!showExcluded && reconcile.pending > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
          data-testid="reconcile-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-900">
                {reconcile.pending} processo(s) de matrícula/rematrícula com plano sem títulos sincronizados
              </p>
              <p className="text-xs text-amber-700">
                {reconcile.missing} título(s) faltando em db.titles. A geração é idempotente: nada é duplicado e títulos pagos não são alterados.
              </p>
            </div>
          </div>
          <Button onClick={() => setReconcileOpen(true)} disabled={reconcile.busy} data-testid="btn-reconcile-titles"
            className="bg-amber-600 hover:bg-amber-700 text-white shrink-0">
            {reconcile.busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
            Ver prévia e gerar títulos
          </Button>
        </div>
      )}

      {!showExcluded && (
        <>
          <TitulosKpiCards summary={summary} active={filters.status} onPick={pickCard} />
          <TitulosFilters filters={filters} set={setF} reset={resetFilters} facets={facets}
            summary={summary} classGroups={classGroups} dirty={dirty} onRefresh={loadData} />
        </>
      )}

      {/* Conteúdo */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {showExcluded ? (
            <div className="overflow-x-auto dna-responsive-table sticky-first-col">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2 p-3 border-b bg-slate-50" data-testid="excluded-filters">
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">De</Label>
                  <Input type="date" value={excludedFilters.deleted_from} onChange={e => setExcludedFilters(f => ({ ...f, deleted_from: e.target.value }))} className="h-8 text-xs" data-testid="excluded-filter-from" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Até</Label>
                  <Input type="date" value={excludedFilters.deleted_to} onChange={e => setExcludedFilters(f => ({ ...f, deleted_to: e.target.value }))} className="h-8 text-xs" data-testid="excluded-filter-to" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Excluído por (e-mail)</Label>
                  <Input placeholder="financeiro@..." value={excludedFilters.deleted_by} onChange={e => setExcludedFilters(f => ({ ...f, deleted_by: e.target.value }))} className="h-8 text-xs" data-testid="excluded-filter-user" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase text-slate-500">Buscar (aluno / motivo / descrição)</Label>
                  <div className="flex gap-1">
                    <Input placeholder="texto livre…" value={excludedFilters.search} onChange={e => setExcludedFilters(f => ({ ...f, search: e.target.value }))} className="h-8 text-xs" data-testid="excluded-filter-search" />
                    <Button type="button" variant="ghost" size="sm" onClick={() => setExcludedFilters({ deleted_from: '', deleted_to: '', deleted_by: '', search: '', student_id: '' })} className="h-8 px-2 text-[11px]" data-testid="excluded-filter-clear">
                      Limpar
                    </Button>
                  </div>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Aluno</TableHead><TableHead>Descrição</TableHead><TableHead>Valor</TableHead>
                    <TableHead>Vencimento</TableHead><TableHead>Status anterior</TableHead><TableHead>Excluído em</TableHead>
                    <TableHead>Por</TableHead><TableHead>Motivo</TableHead><TableHead>Avisos</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingExcluded ? (
                    <TableRow><TableCell colSpan={10} className="text-center py-8"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Carregando…</TableCell></TableRow>
                  ) : excludedItems.length === 0 ? (
                    <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-500">Nenhum título excluído</TableCell></TableRow>
                  ) : excludedItems.map(t => (
                    <TableRow key={t.id} data-testid={`excluded-row-${t.id}`} className="bg-slate-50">
                      <TableCell className="font-medium">{t.student_name || '—'}</TableCell>
                      <TableCell className="max-w-[260px] truncate" title={t.description || ''}>{t.description || '—'}</TableCell>
                      <TableCell>{formatCurrency(t.original_value)}</TableCell>
                      <TableCell>{formatDate(t.due_date)}</TableCell>
                      <TableCell><Badge variant="outline">{t.previous_status || '—'}</Badge></TableCell>
                      <TableCell className="text-xs">{t.deleted_at ? new Date(t.deleted_at).toLocaleString('pt-BR') : '—'}</TableCell>
                      <TableCell className="text-xs">{t.deleted_by_email || '—'}</TableCell>
                      <TableCell className="max-w-[200px] truncate" title={t.delete_reason || ''}>{t.delete_reason || '—'}</TableCell>
                      <TableCell className="text-xs">
                        {t.had_payment && <Badge className="bg-amber-100 text-amber-800 mr-1">Pgto estornado</Badge>}
                        {t.boleto_canceled && <Badge className="bg-rose-100 text-rose-800 mr-1">Boleto cancelado</Badge>}
                        {t.nfse_alert && <Badge className="bg-violet-100 text-violet-800">NFS-e</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => restoreTitles([t.id])} data-testid={`btn-restore-${t.id}`} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                          <Undo2 className="w-3.5 h-3.5 mr-1" /> Restaurar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : titulos.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500" data-testid="titulos-empty">
              <FileText className="w-12 h-12 mb-2 text-slate-300" />
              <p>Nenhum título encontrado com os filtros atuais</p>
              {dirty && <Button variant="link" onClick={resetFilters}>Limpar filtros</Button>}
            </div>
          ) : (
            <>
              {/* Desktop: tabela */}
              <div className="hidden md:block overflow-x-auto dna-responsive-table sticky-first-col">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox checked={titulos.length > 0 && titulos.every(t => selectedIds.has(t.id))}
                          onCheckedChange={toggleSelectAll} data-testid="dnapay-titulos-select-all" />
                      </TableHead>
                      <SortHead field="student">Aluno</SortHead>
                      <TableHead>Responsável</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Parcela</TableHead>
                      <SortHead field="amount">Valor</SortHead>
                      <TableHead>Pago</TableHead>
                      <SortHead field="balance">Saldo</SortHead>
                      <SortHead field="due_date">Vencimento</SortHead>
                      <SortHead field="status">Status</SortHead>
                      <TableHead>Origem</TableHead>
                      <TableHead className="hidden xl:table-cell">Nosso Número</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {titulos.map((t) => (
                      <TableRow key={t.id} data-testid={`titulo-row-${t.id}`}
                        className={`cursor-pointer transition-colors hover:bg-violet-50/60 ${selectedIds.has(t.id) ? 'bg-violet-50' : ''}`}
                        onClick={() => setDetailId(t.id)}>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={selectedIds.has(t.id)} onCheckedChange={() => toggleSelect(t.id)} data-testid={`titulo-select-${t.id}`} />
                        </TableCell>
                        <TableCell>
                          <p className="font-medium text-slate-900">{t.student_name || '—'}</p>
                          <p className="text-[11px] text-slate-500 truncate max-w-[190px]" title={t.class_group_name || ''}>
                            {t.class_group_name || '—'}
                          </p>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{t.responsible_name || '—'}</TableCell>
                        <TableCell>
                          <p className="font-medium text-slate-800">{t.description || '—'}</p>
                          <p className="text-[11px] capitalize text-slate-500">{t.category || '—'}</p>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{t.installment || '—'}</TableCell>
                        <TableCell><ValueCell t={t} /></TableCell>
                        <TableCell className="text-sm text-emerald-700 font-medium">{(t.paid_amount || 0) > 0 ? formatCurrency(t.paid_amount) : '—'}</TableCell>
                        <TableCell className={`text-sm font-semibold ${Number(t.balance) > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                          {formatCurrency(t.balance)}
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(t.due_date)}</TableCell>
                        <TableCell><TituloStatusBadge status={t.status} /></TableCell>
                        <TableCell><Badge variant="outline" className="text-[11px]">{t.source_label}</Badge></TableCell>
                        <TableCell className="hidden xl:table-cell font-mono text-[11px] text-slate-500">{t.nosso_numero || '—'}</TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <ActionsMenu titulo={t} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile: cards com seleção, ordenação e as mesmas ações da tabela */}
              <div className="md:hidden flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-3" data-testid="titulos-mobile-toolbar">
                <label className="inline-flex min-h-[44px] items-center gap-2 text-sm font-medium text-slate-700">
                  <Checkbox checked={titulos.length > 0 && titulos.every(t => selectedIds.has(t.id))}
                    onCheckedChange={toggleSelectAll} data-testid="titulos-mobile-select-all" />
                  Selecionar página
                </label>
                <label className="ml-auto inline-flex items-center gap-2 text-sm text-slate-600">
                  <span>Ordenar</span>
                  <select value={`${sort.field}:${sort.dir}`} data-testid="titulos-mobile-sort"
                    onChange={(e) => { const [field, dir] = e.target.value.split(':'); setSort({ field, dir }); }}
                    className="min-h-[44px] rounded-lg border border-slate-200 bg-white px-2">
                    {[['due_date', 'Vencimento'], ['student', 'Aluno'], ['amount', 'Valor'], ['balance', 'Saldo'], ['status', 'Status']].flatMap(([f, l]) => [
                      <option key={`${f}a`} value={`${f}:asc`}>{l} ↑</option>,
                      <option key={`${f}d`} value={`${f}:desc`}>{l} ↓</option>,
                    ])}
                  </select>
                </label>
              </div>
              <div className="md:hidden divide-y divide-slate-100" data-testid="titulos-mobile-list">
                {titulos.map((t) => (
                  <div key={t.id} className={`flex items-start gap-2 p-3 ${selectedIds.has(t.id) ? 'bg-violet-50' : ''}`} data-testid={`titulo-card-row-${t.id}`}>
                    <div className="flex min-h-[44px] min-w-[32px] items-center justify-center">
                      <Checkbox checked={selectedIds.has(t.id)} onCheckedChange={() => toggleSelect(t.id)}
                        aria-label={`Selecionar título de ${t.student_name || 'aluno'}`} data-testid={`titulo-card-select-${t.id}`} />
                    </div>
                    <button type="button" onClick={() => setDetailId(t.id)}
                      className="min-w-0 flex-1 text-left active:bg-violet-50" data-testid={`titulo-card-${t.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 truncate">{t.student_name || '—'}</p>
                          <p className="text-xs text-slate-500 truncate">{t.description || '—'}{t.installment ? ` · ${t.installment}` : ''}</p>
                          {t.responsible_name && <p className="text-xs text-slate-500 truncate">Resp.: {t.responsible_name}</p>}
                        </div>
                        <TituloStatusBadge status={t.status} />
                      </div>
                      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 text-sm">
                        <span className="font-bold text-slate-900">{formatCurrency(t.amount_display)}</span>
                        <span className="text-slate-500">venc. {formatDate(t.due_date)}</span>
                      </div>
                      {(t.paid_amount || 0) > 0 && (
                        <p className="mt-1 text-[11px] text-violet-600">
                          pago {formatCurrency(t.paid_amount)} · saldo {formatCurrency(t.balance)}
                        </p>
                      )}
                      {t.source_label && <Badge variant="outline" className="mt-1 text-[11px]">{t.source_label}</Badge>}
                    </button>
                    <div className="flex min-h-[44px] items-center" onClick={(e) => e.stopPropagation()}>
                      <ActionsMenu titulo={t} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Paginação */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-3"
                   data-testid="titulos-pagination">
                <p className="text-xs text-slate-500">
                  Página <b>{page}</b> de <b>{totalPages}</b> · {summary?.count ?? 0} títulos no filtro
                </p>
                <div className="flex items-center gap-2">
                  <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                    <SelectTrigger className="h-8 w-[110px] text-xs" data-testid="titulos-page-size"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="50">50 / página</SelectItem>
                      <SelectItem value="100">100 / página</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="titulos-prev-page">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="titulos-next-page">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Painel lateral de detalhes */}
      <TituloDetailDrawer titleId={detailId} open={!!detailId} onClose={() => setDetailId(null)} />

      {/* Modal Novo Título */}
      <Dialog open={showNovoTitulo} onOpenChange={setShowNovoTitulo}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Novo Título</DialogTitle>
            <DialogDescription>Crie uma nova cobrança para um aluno</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Aluno *</Label>
              <Select value={novoTitulo.aluno_id} onValueChange={(v) => setNovoTitulo({ ...novoTitulo, aluno_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecione o aluno" /></SelectTrigger>
                <SelectContent>
                  {alunos.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descrição *</Label>
              <Input value={novoTitulo.descricao} onChange={(e) => setNovoTitulo({ ...novoTitulo, descricao: e.target.value })} placeholder="Ex: Mensalidade Abril/2026" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoria</Label>
                <Select value={novoTitulo.categoria} onValueChange={(v) => setNovoTitulo({ ...novoTitulo, categoria: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensalidade">Mensalidade</SelectItem>
                    <SelectItem value="matricula">Matrícula</SelectItem>
                    <SelectItem value="material">Material</SelectItem>
                    <SelectItem value="evento">Evento</SelectItem>
                    <SelectItem value="outros">Outros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Competência</Label>
                <Input type="month" value={novoTitulo.competencia} onChange={(e) => setNovoTitulo({ ...novoTitulo, competencia: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valor *</Label>
                <Input type="number" value={novoTitulo.valor_original} onChange={(e) => setNovoTitulo({ ...novoTitulo, valor_original: e.target.value })} placeholder="0,00" />
              </div>
              <div>
                <Label>Desconto</Label>
                <Input type="number" value={novoTitulo.desconto} onChange={(e) => setNovoTitulo({ ...novoTitulo, desconto: e.target.value })} placeholder="0,00" />
              </div>
            </div>
            <div>
              <Label>Vencimento *</Label>
              <Input type="date" value={novoTitulo.vencimento} onChange={(e) => setNovoTitulo({ ...novoTitulo, vencimento: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovoTitulo(false)}>Cancelar</Button>
            <Button onClick={handleCriarTitulo}>Criar Título</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Pagamento */}
      <Dialog open={showPagamento} onOpenChange={(open) => { setShowPagamento(open); if (!open) { setPixData(null); setBoletoData(null); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Gerar Pagamento</DialogTitle>
            <DialogDescription>
              {selectedTitulo?.description} - {formatCurrency(selectedTitulo?.balance ?? selectedTitulo?.amount_display)}
            </DialogDescription>
          </DialogHeader>
          {!pixData && !boletoData ? (
            <div className="space-y-4">
              <div>
                <Label>Método de Pagamento</Label>
                <Select value={metodoPagamento} onValueChange={setMetodoPagamento}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pix"><div className="flex items-center gap-2"><QrCode className="w-4 h-4" /> PIX (Instantâneo)</div></SelectItem>
                    <SelectItem value="boleto"><div className="flex items-center gap-2"><Barcode className="w-4 h-4" /> Boleto Bancário</div></SelectItem>
                    <SelectItem value="cartao_credito"><div className="flex items-center gap-2"><CreditCard className="w-4 h-4" /> Cartão de Crédito</div></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowPagamento(false)}>Cancelar</Button>
                <Button onClick={handleGerarPagamento}>Gerar</Button>
              </DialogFooter>
            </div>
          ) : pixData ? (
            <div className="space-y-4">
              <div className="flex justify-center"><img src={pixData.qr_code} alt="QR Code PIX" className="w-48 h-48" /></div>
              <div>
                <Label>Código PIX (Copia e Cola)</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={pixData.copia_cola} readOnly className="text-xs" />
                  <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(pixData.copia_cola); toast.success('Código PIX copiado!'); }}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="text-center text-sm text-slate-500">
                <p>Valor: <strong className="text-slate-900">{formatCurrency(pixData.valor)}</strong></p>
                <p>Vencimento: {formatDate(pixData.vencimento)}</p>
              </div>
              <DialogFooter><Button onClick={() => { setShowPagamento(false); setPixData(null); }}>Fechar</Button></DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <div>
                  <Label className="text-xs text-slate-500">Nosso Número</Label>
                  <p className="font-mono">{boletoData.nosso_numero}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Linha Digitável</Label>
                  <p className="font-mono text-sm break-all">{boletoData.linha_digitavel}</p>
                </div>
              </div>
              <div className="text-center text-sm text-slate-500">
                <p>Valor: <strong className="text-slate-900">{formatCurrency(boletoData.valor)}</strong></p>
                <p>Vencimento: {formatDate(boletoData.vencimento)}</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { navigator.clipboard.writeText(boletoData.linha_digitavel); toast.success('Linha digitável copiada!'); }}>
                  <Copy className="w-4 h-4 mr-2" /> Copiar Linha
                </Button>
                <Button onClick={() => { setShowPagamento(false); setBoletoData(null); }}>Fechar</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Exclusão Financeira Segura */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => { if (!deleteDialog.loading) setDeleteDialog(d => ({ ...d, open: o })); }}>
        <DialogContent className="max-w-2xl" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" /> Exclusão financeira segura
            </DialogTitle>
            <DialogDescription>
              Soft delete · reversível · auditável. Os títulos sairão de Caixa, DRE, DRE IA, Painel Global, Executivo, Analytics, Tributário, Fiscal, Lembretes, Cobrança, DNA Connect e relatórios.
            </DialogDescription>
          </DialogHeader>
          {deleteDialog.loading && !deleteDialog.preview && (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Calculando impacto…</div>
          )}
          {deleteDialog.preview && (
            <div className="space-y-3">
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5">
                <p className="text-sm font-bold text-rose-900">{deleteDialog.preview.would_delete} título(s) serão excluído(s) do financeiro</p>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
                {[
                  ['Pagos', deleteDialog.preview.counts?.paid, 'text-emerald-700'],
                  ['Abertos', deleteDialog.preview.counts?.open, 'text-amber-700'],
                  ['Vencidos', deleteDialog.preview.counts?.overdue, 'text-rose-700'],
                  ['c/ Boleto', deleteDialog.preview.counts?.with_boleto, 'text-blue-700'],
                  ['c/ Pix', deleteDialog.preview.counts?.with_pix, 'text-cyan-700'],
                  ['c/ NFS-e', deleteDialog.preview.counts?.with_nfse, 'text-violet-700'],
                ].map(([label, val, cls]) => (
                  <div key={label} className="rounded border border-slate-200 px-2 py-1.5">
                    <p className="text-[10px] uppercase text-slate-500">{label}</p>
                    <p className={`text-lg font-bold ${cls}`}>{val ?? 0}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded bg-slate-50 px-2 py-1.5"><span className="text-slate-500 text-xs">Valor total</span><br /><span className="font-bold">{formatCurrency(deleteDialog.preview.counts?.value_total)}</span></div>
                <div className="rounded bg-emerald-50 px-2 py-1.5"><span className="text-emerald-700 text-xs">Valor pago a estornar</span><br /><span className="font-bold text-emerald-700">{formatCurrency(deleteDialog.preview.counts?.value_paid)}</span></div>
              </div>
              <div>
                <Label htmlFor="delete-reason" className="text-sm">Motivo (obrigatório) *</Label>
                <Textarea id="delete-reason" value={deleteDialog.reason} rows={2}
                  onChange={e => setDeleteDialog(d => ({ ...d, reason: e.target.value }))}
                  placeholder="Ex: erro de lançamento, duplicidade de importação, aluno transferido…"
                  className="mt-1" data-testid="delete-reason-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(d => ({ ...d, open: false }))} disabled={deleteDialog.loading}>Cancelar</Button>
            <Button onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-confirm-btn"
              disabled={deleteDialog.loading || !deleteDialog.preview || (deleteDialog.preview.would_delete || 0) === 0 || !deleteDialog.reason.trim()}>
              {deleteDialog.loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Confirmar exclusão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <WhatsappSendDialog titleId={waTitleId} open={!!waTitleId} onClose={() => setWaTitleId(null)} />
      <ReciboUnificadoDialog open={!!unified} onOpenChange={(v) => !v && setUnified(null)}
        studentId={unified?.id} studentName={unified?.name} />
      <DeclaracaoIrDialog open={!!irDecl} onOpenChange={(v) => !v && setIrDecl(null)}
        studentId={irDecl?.id} studentName={irDecl?.name} />

      <PaymentSettlementModal open={!!settle} onOpenChange={(v) => !v && setSettle(null)}
        titleId={settle?.id} title={settle?.description}
        onSettled={() => { setSelectedIds(new Set()); loadData(); }} />

      <BatchSettlementModal open={batchOpen} onOpenChange={setBatchOpen}
        titleIds={Array.from(selectedIds)}
        onSettled={() => { setSelectedIds(new Set()); loadData(); }} />

      <ReconciliarDatasDialog open={datasOpen} onOpenChange={setDatasOpen} onDone={loadData} />

      <TituloRecebimentos titleId={recebimentosId}
        onOpenChange={(v) => { if (!v) setRecebimentosId(null); }} onChanged={loadData} />

      {/* Renegociação */}
      <Dialog open={renegDialog.open} onOpenChange={(o) => !o && !renegDialog.busy && setRenegDialog(d => ({ ...d, open: false }))}>
        <DialogContent className="sm:max-w-md" data-testid="reneg-dialog">
          <DialogHeader>
            <DialogTitle>Renegociar título</DialogTitle>
            <DialogDescription>
              O título atual será marcado como <b>renegociado</b> e um novo título vinculado será criado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Novo valor *</Label>
                <Input type="number" step="0.01" value={renegDialog.novo_valor} data-testid="reneg-valor"
                  onChange={e => setRenegDialog(d => ({ ...d, novo_valor: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Novo vencimento *</Label>
                <Input type="date" value={renegDialog.novo_vencimento} data-testid="reneg-vencimento"
                  onChange={e => setRenegDialog(d => ({ ...d, novo_vencimento: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Motivo *</Label>
              <Input value={renegDialog.motivo} placeholder="Mínimo 3 caracteres" data-testid="reneg-motivo"
                onChange={e => setRenegDialog(d => ({ ...d, motivo: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={renegDialog.busy} onClick={() => setRenegDialog(d => ({ ...d, open: false }))}>Cancelar</Button>
            <Button onClick={confirmReneg} disabled={renegDialog.busy} data-testid="reneg-confirm" className="bg-sky-600 hover:bg-sky-700">
              {renegDialog.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar renegociação'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {tituloActionDialog}
      <PlanoAlunoModal open={!!planoTitulo} titleId={planoTitulo?.title_id}
        onOpenChange={(v) => { if (!v) setPlanoTitulo(null); }}
        onSaved={loadData}
        onEditTitleOnly={planoTitulo && (planoTitulo.status !== 'pago' || Number(planoTitulo.balance ?? 0) > 0.009) ? (() => { const t = planoTitulo; openTituloAction('edit', legacyShape(t)); }) : undefined} />
      <ParcelasDivergentesDialog open={showParcelasDiv} onOpenChange={setShowParcelasDiv} onFixed={loadData} />

      <ReconcilePreviewDialog open={reconcileOpen} onClose={() => setReconcileOpen(false)}
        onDone={() => { loadData(); }} />
      <PurgeBulkDialog open={purgeOpen} titleIds={Array.from(selectedIds)}
        onClose={() => setPurgeOpen(false)} onDone={() => { setSelectedIds(new Set()); loadData(); }} />

      <Dialog open={showPurged} onOpenChange={(o) => !o && setShowPurged(false)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-auto" data-testid="purged-list-dialog">
          <DialogHeader>
            <DialogTitle>Títulos purgados (auditoria)</DialogTitle>
            <DialogDescription>
              Removidos definitivamente do operacional. A evidência administrativa fica registrada aqui e em finance_events (TITLE_PURGED).
            </DialogDescription>
          </DialogHeader>
          <table className="w-full text-[12px]">
            <thead className="text-[10px] uppercase text-slate-400">
              <tr><th className="text-left">Título</th><th className="text-left">Aluno</th><th className="text-left">Valor</th>
                <th className="text-left">Venc.</th><th className="text-left">Motivo</th><th className="text-left">Quem</th>
                <th className="text-left">Quando</th><th className="text-left">Processo</th></tr>
            </thead>
            <tbody>
              {purgedList.map(p => (
                <tr key={p.id} className="border-t border-slate-50" data-testid={`purged-row-${p.title_id}`}>
                  <td className="font-mono text-[10px]">{p.title_id?.slice(0, 8)}</td>
                  <td>{p.student_name || '—'}</td>
                  <td>R$ {Number(p.amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                  <td>{p.due_date || '—'}</td>
                  <td className="max-w-[220px]">{p.purge_reason}</td>
                  <td>{p.purged_by}</td>
                  <td>{String(p.purged_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td className="font-mono text-[10px]">{p.enrollment_process_id?.slice(0, 8) || '—'}</td>
                </tr>
              ))}
              {purgedList.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-slate-400">Nenhum título purgado.</td></tr>}
            </tbody>
          </table>
        </DialogContent>
      </Dialog>
    </div>
  );
}
